//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React, { Component } from 'react';
import _ from 'underscore';
import { Button, Icon, Spinner } from 'unison';

import RequiredPermissionsButton from '@components/atoms/required-permissions-button/RequiredPermissionsButton';
import styled from 'styled-components';
import theme from '@theme/theme';
import { PrimaryButton } from '@components/atoms/button/Button';
import { isDefined } from '@utils/filterPredicates';

interface IItem {
  name: string;
  id: string | number;
}

interface IOwnProps {
  createButtonLabel: string;
  createPermission: string | undefined;
  createPlaceholderText: string;
  itemsAsyncOutstanding: Set<IItem['id']>;
  listItems: IItem[];
  menuTitle: string;
  noItemsText: string;
  onCreate: (name: string) => any;
  onSelect: (item: IItem) => any;
  searchPlaceholderText: string;
  selectedItems: object;
  showCreateLoadingSpinner: boolean;
  onAlreadyAdded?: (item: IItem) => any;
}

interface IOwnState {
  showCreateButton: boolean;
  searchText: string;
  createText: string;
}

export class SearchAndAddMenu extends Component<IOwnProps, IOwnState> {
  private containerRef = React.createRef<HTMLDivElement>();

  public state: Readonly<IOwnState> = {
    showCreateButton: true,
    searchText: '',
    createText: '',
  };

  public renderListItem(item: IItem) {
    const { selectedItems, itemsAsyncOutstanding } = this.props;

    return (
      <a
        key={item.id}
        tabIndex={-1}
        onClick={() => this.selectClicked(item)}
        className='cu-list-item cu-align-items-center cu-justify-content-between'
      >
        <ListItem>{item.name}</ListItem>
        <span>
          {itemsAsyncOutstanding.has(item.id) ? (
            <InlineSpinner />
          ) : (
            <span>
              {selectedItems[item.id] ? (
                <Icon name='checkmark' className='cu-icon-checkmark' />
              ) : undefined}
            </span>
          )}
        </span>
      </a>
    );
  }

  public renderListItems() {
    const { listItems } = this.props;
    const { searchText } = this.state;

    if (listItems === undefined) {
      return undefined;
    }

    let filteredItems = listItems;
    if (searchText !== '') {
      filteredItems = listItems.filter((item) =>
        item.name.toLowerCase().includes(searchText.toLowerCase()),
      );
      if (filteredItems && filteredItems.length > 0) {
        filteredItems = _(filteredItems).sortBy('name');
      }
    }

    if (!filteredItems || filteredItems.length === 0) {
      return (
        <div>
          <span className='no-items-text'>No results found.</span>
        </div>
      );
    }
    return filteredItems.map((item) => this.renderListItem(item));
  }

  public createClicked = (e) => {
    const { listItems } = this.props;
    const { createText } = this.state;
    const names = listItems.map((i) => i.name);
    if (createText && createText.trim() !== '' && !names.includes(createText)) {
      this.props.onCreate(createText);
    }
    this.setState({ searchText: '', createText: '' });
    e.preventDefault();
    return false;
  };

  public clearButtonClicked = () => {
    this.setState({ searchText: '' });
  };

  // When the ng-switch removes the create button, focus returns to the body and closes the menu.
  // So we are returning the focus to something that isn't removed from the DOM.
  public returnFocus = () => {
    if (this.containerRef.current) {
      this.containerRef.current.focus();
    }
  };

  public createButtonPressed = () => {
    this.returnFocus();
    this.setState({ showCreateButton: false });
  };

  public selectClicked = (item: IItem) => {
    const { onSelect, itemsAsyncOutstanding, selectedItems, onAlreadyAdded } =
      this.props;

    if (selectedItems[item.id]) {
      if (onAlreadyAdded) {
        onAlreadyAdded(item);
      }
    } else if (!itemsAsyncOutstanding.has(item.id)) {
      onSelect(item);
    }
  };

  public renderListItemsMenu() {
    const { listItems, noItemsText, searchPlaceholderText } = this.props;
    const { searchText } = this.state;
    return (
      <div>
        {listItems && listItems.length === 0 ? (
          <div className='empty-list-state'>
            <span className='no-items-text'>{noItemsText}</span>
          </div>
        ) : (
          <div>
            <div className='search-form cu-d-flex cu-my-2'>
              <Icon className='search-icon' name='search' />
              <input
                className='cu-form-control cu-flex-1'
                value={searchText}
                onChange={(e) => this.setState({ searchText: e.target.value })}
                placeholder={searchPlaceholderText}
                type='text'
              />
              {searchText.length > 0 ? (
                <Icon
                  className='clear-icon'
                  name='close'
                  onClick={this.clearButtonClicked}
                />
              ) : undefined}
            </div>
            <div className='items-list'>{this.renderListItems()}</div>
          </div>
        )}
      </div>
    );
  }

  public renderCreateSection() {
    const {
      createButtonLabel,
      createPlaceholderText,
      showCreateLoadingSpinner,
      createPermission,
    } = this.props;
    const { showCreateButton, createText } = this.state;
    return (
      <div className='create-container' tabIndex={-1} ref={this.containerRef}>
        {showCreateButton ? (
          <div ng-switch-when='true'>
            {isDefined(createPermission) ? (
              <RequiredPermissionsButton
                permissionName={createPermission}
                onClick={this.createButtonPressed}
                className='create-button cu-align-center cu-pl-2 cu-pr-3 cu-my-3'
              >
                <Icon name='add-with-circle' className='create-icon cu-pr-2' />
                <span className='create-text'>{createButtonLabel}</span>
              </RequiredPermissionsButton>
            ) : (
              <PrimaryButton
                onClick={this.createButtonPressed}
                className='create-button cu-align-center cu-pl-2 cu-pr-3 cu-my-3'
              >
                <Icon name='add-with-circle' className='create-icon cu-pr-2' />
                <span className='create-text'>{createButtonLabel}</span>
              </PrimaryButton>
            )}
          </div>
        ) : (
          <form className='create-form cu-mb-2' onSubmit={this.createClicked}>
            <div className='cu-text-table-header cu-ml-1 cu-mb-3 cu-mt-3'>
              {createButtonLabel}
            </div>
            <div className='create-input cu-d-flex cu-mt-1'>
              <input
                className='cu-form-control cu-flex-1'
                value={createText}
                onChange={(e) => this.setState({ createText: e.target.value })}
                placeholder={createPlaceholderText}
                type='text'
              />
              {showCreateLoadingSpinner ? (
                <InlineSpinner className='add-text' />
              ) : undefined}
              {createButtonLabel && !showCreateLoadingSpinner ? (
                <Button className='add-text' onClick={this.createClicked}>
                  Add
                </Button>
              ) : undefined}
            </div>
          </form>
        )}
      </div>
    );
  }
  public render() {
    const { menuTitle } = this.props;
    return (
      <SearchAddMenuContainer>
        <div className='cu-text-table-header cu-ml-1 cu-mb-3 cu-mt-2'>
          {menuTitle}
        </div>
        {this.renderListItemsMenu()}
        <CreateListBorder />
        {this.renderCreateSection()}
      </SearchAddMenuContainer>
    );
  }
}

const SearchAddMenuContainer = styled.div.attrs({
  className: 'search-and-add-menu cu-p-3',
})`
  text-align: left;
`;

const InlineSpinner = styled(Spinner)`
  margin: 0px !important;
  width: 14px !important;
`;

const CreateListBorder = styled.div`
  border-bottom: ${theme.borderWidths[1]} solid ${theme.colors.gray2};
  margin-top: ${theme.spacings[2]};
`;

const ListItem = styled.span.attrs({
  className: 'item-span',
})`
  cursor: pointer;
`;

export default SearchAndAddMenu;
